import { Box, Breadcrumbs, Button, FormControl, InputAdornment, InputLabel, MenuItem, Paper, Select, Stack, TextField, Toolbar, Typography, useMediaQuery } from "@mui/material";
import { useContext, useEffect, useState } from "react";
import MainHeader from "../components/MainHeader";
import Main from "../components/Main";
import { Link } from "react-router-dom";
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import SearchIcon from '@mui/icons-material/Search';
import ClientsTable from "../components/tables/ClientsTable";
import SideMenu from "../components/SideMenu";
import AppNavBar from "../components/AppNavBar";
import AppBottomNavigation from "../components/AppBottomNavigation";

import { exportClients, fetchClients } from "../services/ClientsService";
import { AuthContext } from "../utils/AuthContext";

import 'react-toastify/dist/ReactToastify.css';
import { toast } from 'react-toastify';

import moment from 'moment';
import { useTheme } from "@emotion/react";
import { useQuery } from "@tanstack/react-query";
import ClientInformationDialog from "../components/dialogs/ClientInformationDialog";
import getHomeLink from "../utils/getHomeLink";
import CheckIcon from '@mui/icons-material/Check';
import { updateTimezone } from "../services/UsersService";


const Clients = () => {
    const theme = useTheme();
    const isMediumScreen = useMediaQuery(theme.breakpoints.up("md"));

    const [open, setOpen] = useState(localStorage.getItem('drawer-open') == 'true');



    const { auth } = useContext(AuthContext);

    const [searchClient, setSearchClient] = useState('');
    const [searchClientDebounced, setSearchClientDebounced] = useState('');
    const [filterBy, setFilterBy] = useState(localStorage.getItem('agent_connect_clients_filter_by') || getDefaultFilterBy());

    const [rows, setRows] = useState([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(parseInt(localStorage.getItem('agent_connect_clients_rows_per_page') || '10'));
    const [totalRecords, setTotalRecords] = useState(0);
    const [isLoading, setIsLoading] = useState(false);

    const [isExporting, setIsExporting] = useState(false);

    const [selectedClient, setSelectedClient] = useState(null);
    const [showClientInfoDialog, setShowClientInfoDialog] = useState(false);

    const { data: clientsData, isFetching } = useQuery(
        {
            queryKey: ["clients_data", page, rowsPerPage, searchClientDebounced, filterBy, auth?.user?.id],
            queryFn: () => fetchClients({
                filterBy,
                token: auth.token,
                searchQuery: searchClient,
                page: page + 1,
                pageSize: rowsPerPage
            })
        });

    // useEffect(() => {
    //     handleFetchClients();
    // }, []);

    // useEffect(() => {
    //     handleFetchClients();
    // }, [page, rowsPerPage]);

    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            // handleFetchClients();
            setSearchClientDebounced(searchClient);
        }, 1000)

        return () => clearTimeout(delayDebounceFn)
    }, [searchClient]);

    // Update user's timezone
    useEffect(() => {
        const saveTimezone = async () => {
            try {
                // Get user's timezone using Intl API
                const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

                await updateTimezone({ token: auth.token, data: { timezone } });
            } catch (error) {
                console.error('Error saving timezone:', error);
            }
        };

        saveTimezone();
    }, []);

    function getDefaultFilterBy() {
        if (auth?.user?.user_type === 'Agent') {
            return 'referred_client';
        } else if (auth?.user?.user_type === 'Admin') {
            return 'agent_id';
        }
    }

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);

        localStorage.setItem('agent_connect_clients_rows_per_page', event.target.value);
    };

    const toggleDrawer = () => {
        localStorage.setItem('drawer-open', !open);
        setOpen(!open);
    };

    function handleClick(event) {
        event.preventDefault();
        console.info('You clicked a breadcrumb.');
    }

    const breadcrumbs = [
        auth.user.user_type === 'Admin' ? <Typography key="2" sx={{ color: 'text.primary' }}>
            Home
        </Typography> : <Link sx={{ textDecoration: 0 }} key="1" color="inherit" to={getHomeLink(auth)}>
            Home
        </Link>,
        <Typography key="2" sx={{ color: 'text.primary' }}>
            Clients
        </Typography>,
    ];

    async function handleFetchClients() {
        setIsLoading(true);
        fetchClients({
            filterBy,
            token: auth.token,
            searchQuery: searchClient,
            page: page + 1,
            pageSize: rowsPerPage
        }).then(res => {
            setRows(res.data.commissions);
            setTotalRecords(res.data.total);
            setIsLoading(false);
        }).catch(error => {
            console.error("Error fetching clients:", error);
            setIsLoading(false);
        });
    }

    const handleExportClients = async () => {
        let loadingToastId = null;

        try {
            // Show loading toast without a timer
            loadingToastId = toast.info("Exporting clients...", { autoClose: false });

            const token = auth.token;
            const filters = {
                filterBy,
                searchQuery: searchClient,
            };

            setIsExporting(true);

            const blob = await exportClients({ token, filters });

            setIsExporting(false);


            // Ensure the response is handled as a blob
            const url = window.URL.createObjectURL(new Blob([blob]));
            const a = document.createElement('a');
            a.href = url;
            a.download = "Clients_Export_" + moment().format('YYYY_MM_DD') + ".csv";
            document.body.appendChild(a);
            a.click();
            a.remove();

            // Dismiss the loading toast after the export process succeeds
            if (loadingToastId) {
                toast.dismiss(loadingToastId);
            }

            toast.success("Clients exported", {
                autoClose: 2500, hideProgressBar: true,
                icon: ({ theme, type }) => <CheckIcon sx={{ color: '#3328BF' }} />,
                closeButton: false
            });
        } catch (error) {
            console.error('Error exporting clients:', error);
            toast.error("Error exporting clients. Please try again."); // Show error toast notification
        }
    };

    return (
        <Box sx={{
            backgroundColor: '#ededed',
            height: '100vh',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden', // Ensure no vertical scrollbar
        }}>
            <AppNavBar currentPage='clients' />
            <Box sx={{ display: 'flex', flexGrow: 1, overflow: 'hidden' }}>
                <SideMenu currentPage='clients' />
                <Main open={open}
                    sx={{
                        // height: '100%',
                        overflow: 'hidden',
                        marginLeft: {
                            xs: 0,
                            md: 1
                        },
                        backgroundColor: '#fff',
                        mt: 7,
                        mb: 2,
                        marginRight: {
                            xs: 0,
                            md: 1
                        },
                        borderRadius: {
                            xs: 0,
                            md: 2
                        },
                        flexGrow: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        px: {
                            xs: 2,
                            md: 3
                        },
                        py: 2
                    }} >

                    <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', mb: 1 }}>
                        <Box>
                            <Typography variant="h1" sx={{ fontSize: 24, mb: 1 }}>
                                Clients
                            </Typography>
                            <Stack sx={{
                                display: {
                                    xs: 'none',
                                    md: 'block'
                                }
                            }} spacing={2}>
                                <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb">
                                    {breadcrumbs}
                                </Breadcrumbs>
                            </Stack>
                        </Box>
                    </Box>
                    <Box sx={{
                        width: '100%', mb: '15px', display: 'flex', flexDirection: 'row', justifyContent: {
                            xs: 'left',
                            md: 'right'
                        }, gap: 2
                    }}>

                        <TextField fullWidth={!isMediumScreen} className="search" sx={{ mt: 1, ml: 1 }} size='small' variant='standard' placeholder="Search..." InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            )
                        }}
                            value={searchClient}
                            onChange={(e) => {
                                setSearchClient(e.target.value);
                            }}
                        />
                        <Box>
                            <FormControl size="small" variant="outlined">
                                <InputLabel id="filter-by-select-label">Filter By</InputLabel>
                                <Select
                                    labelId="filter-by-select-label"
                                    id="filter-by-select"
                                    label="Filter By"
                                    value={filterBy}
                                    onChange={(e) => {
                                        const filter_by = e.target.value;
                                        setFilterBy(filter_by)
                                        localStorage.setItem('agent_connect_clients_filter_by', filter_by);
                                    }}
                                >
                                    {
                                        auth?.user?.user_type === 'Admin' &&
                                        <MenuItem value={'agent_id'}>Agent ID</MenuItem>
                                    }

                                    <MenuItem value={'cs_no'}>CS No</MenuItem>
                                    <MenuItem value={'commission_type'}>Type</MenuItem>
                                    <MenuItem value={'referred_client'}>Client Name</MenuItem>
                                    <MenuItem value={'membership_type'}>Referral</MenuItem>
                                    <MenuItem value={'insurer'}>Insurer</MenuItem>
                                    <MenuItem value={'status'}>Status</MenuItem>
                                </Select>
                            </FormControl>
                        </Box>
                        <Button sx={{
                            display: {
                                xs: 'none',
                                md: 'block'
                            }
                        }}
                            variant="contained"
                            onClick={handleExportClients}
                            disabled={isExporting}
                        >Export</Button>
                    </Box>
                    <ClientsTable
                        clients={clientsData ? clientsData.commissions : []}
                        page={page}
                        rowsPerPage={rowsPerPage}
                        totalRecords={clientsData ? clientsData.total : 0}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        isLoading={isFetching}
                        setShowClientInfoDialog={setShowClientInfoDialog}
                        setSelectedClient={setSelectedClient}
                    />

                    {/* Bottom Navigation */}
                    <Paper sx={{
                        zIndex: 9997,
                        display: {
                            xs: 'block',
                            md: 'none'
                        }, position: 'fixed', bottom: 0, left: 0, right: 0
                    }} elevation={3}>
                        <AppBottomNavigation currentPage="clients" />
                    </Paper>


                    <ClientInformationDialog
                        dialogOpen={showClientInfoDialog}
                        setDialogOpen={setShowClientInfoDialog}
                        selectedClient={selectedClient}
                    />
                </Main>
            </Box>
        </Box>
    );
};

export default Clients;